 // import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { MessageCacheProvider } from './contexts/MessageCacheContext';
import { VoiceChatProvider } from './contexts/VoiceChatContext';
import { WritebackProvider } from './contexts/WritebackContext';
import Navigation from './components/Navigation';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import WebTopBar from './components/WebTopBar';
import HistoryDrawer from './components/HistoryDrawer';
import DowngradeTakeover from './components/remember/DowngradeTakeover';
import MessageRequestPrompt from './components/MessageRequestPrompt';
import Home from './pages/Home';
import Download from './pages/Download';
import Faq from './pages/Faq';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmailAction from './pages/VerifyEmailAction';
import MessageDetail from './pages/MessageDetail';
import UserProfile from './pages/UserProfile.tsx';
import F8Chat from './pages/F8Chat';
import ChatHistory from './pages/ChatHistory';
import FindPpl from './pages/FindPpl.tsx';
import CirclesList from './pages/CirclesList.tsx';
import CircleDetail from './pages/CircleDetail.tsx';
import CircleChat from './pages/CircleChat.tsx';
import CircleEvents from './pages/CircleEvents.tsx';
import CircleMembers from './pages/CircleMembers.tsx';
import CircleInviteByToken from './pages/CircleInviteByToken.tsx';
import CreateCircle from './pages/CreateCircle.tsx';
import EditProfile from './pages/EditProfile.tsx';
import Profile from './pages/Profile.tsx';
import Settings from './pages/Settings.tsx';
import Onboarding from './pages/Onboarding.tsx';
import Messages from './pages/Messages.tsx';
// Conversation is now embedded within the Messages split-view
import Premium from './pages/Premium.tsx';
import PremiumCallback from './pages/PremiumCallback.tsx';
import SubscriptionManagement from './pages/SubscriptionManagement.tsx';
import Contacts from './pages/Contacts.tsx';
import Insights from './pages/Insights.tsx';
import ContactDetail from './pages/ContactDetail.tsx';
import Unsubscribe from './pages/Unsubscribe';
import RoomErrorBoundary from './components/RoomErrorBoundary';
import RoomPlaceholder from './pages/RoomPlaceholder.tsx';
import RoomList from './pages/RoomList.tsx';
import RoomDetail from './pages/RoomDetail.tsx';
import RoomInviteByToken from './pages/RoomInviteByToken.tsx';
import RoomSoloPrep from './pages/RoomSoloPrep.tsx';
import RoomInvitationReceived from './pages/RoomInvitationReceived.tsx';
import RoomTellMore from './pages/RoomTellMore.tsx';
import RoomSharingRules from './pages/RoomSharingRules.tsx';
import RoomOpener from './pages/RoomOpener.tsx';
import RoomSession from './pages/RoomSession.tsx';
import RoomCheckin from './pages/RoomCheckin.tsx';
import RoomEnd from './pages/RoomEnd.tsx';
import RoomSummary from './pages/RoomSummary.tsx';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Admin from './pages/Admin';
import { capture as phCapture } from './services/posthog';
import './styles/variables.css';
import './styles/Navigation.css';
import './App.css';
import './styles/pplTalkInteractions.css';
import './styles/Posts.css';
import './styles/profileAndFindPpl.css';
import './styles/circles.css';
import './styles/account.css';
import './styles/social.css';
import './styles/premium.css';
// Import legacy static site styles last so they override defaults
import './styles/admin.css';
import '../css/styles.css';
import { useState, useEffect, lazy, Suspense } from 'react';

// PPL-407 — DEV-ONLY contact sheet for the home kit + module registry.
// The ternary is folded to `null` in production builds (import.meta.env.DEV
// is a build-time constant), so the fixtures chunk is never emitted. Not a
// nav change: nothing links here.
const HomeFixturesPage = import.meta.env.DEV
  ? lazy(() => import('./components/Home/__fixtures__/HomeFixturesPage'))
  : null;

// Aurora animated background
function AuroraBackground() {
  return (
    <div className="aurora-bg">
      <div className="aurora-blob-1" />
      <div className="aurora-blob-2" />
      <div className="aurora-overlay" />
    </div>
  );
}

// Pages that use the app shell layout (left rail + aurora)
const APP_SHELL_PATHS = [
  '/', '/posts', '/new', '/discussed',
  '/f8chat',
  '/find-ppl', '/meet-ppl', '/connect-with',
  '/circles',
  '/contacts',
  // PPL-265 — the write-back review queue lives inside my ppl.
  '/insights',
  // PPL-395 — /profile is the read view of your own profile; /profile/edit is
  // the form behind its "edit profile" button. isAppShellPath exact-matches
  // this list, so both paths have to be listed literally.
  '/profile', '/profile/edit', '/settings', '/onboarding',
  '/messages',
  '/premium', '/premium/callback',
  '/settings/subscription',
  '/admin',
  '/chat-history',
  // PPL-17 — authenticated mediation routes use the app shell. The PUBLIC
  // /i/:token route deliberately does NOT — it's a bare anonymous landing,
  // like /unsubscribe.
  '/mediation',
  '/rooms',
];

function isAppShellPath(pathname) {
  if (APP_SHELL_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/talk/')) return true;
  if (pathname.startsWith('/user/')) return true;
  // PPL-215 — /circles/* uses the shell; the PUBLIC /c/:token circle-invite
  // landing deliberately does NOT (bare anonymous route, like /i/:token).
  if (pathname.startsWith('/circles/')) return true;
  if (pathname.startsWith('/messages/')) return true;
  if (pathname.startsWith('/contacts/')) return true;
  // PPL-17 — every /mediation/* leaf uses the shell EXCEPT /i/:token, which is
  // a deliberate bare anonymous route and is handled outside ProtectedRoute.
  if (pathname.startsWith('/mediation/')) return true;
  // PPL-48 — /rooms/* mirrors /mediation/* as the canonical path going forward.
  if (pathname.startsWith('/rooms/')) return true;
  return false;
}

const ROUTE_TITLES = {
  '/': { title: 'Posts', subtitle: 'What\'s on your mind' },
  '/posts': { title: 'Posts', subtitle: 'What\'s on your mind' },
  '/new': { title: 'Posts', subtitle: 'Latest' },
  '/discussed': { title: 'Posts', subtitle: 'Most discussed' },
  '/f8chat': { title: 'Chat with f8', subtitle: 'Your AI companion' },
  '/find-ppl': { title: 'Discover', subtitle: 'Find new people' },
  '/meet-ppl': { title: 'Discover', subtitle: 'Find new people' },
  '/connect-with': { title: 'Discover', subtitle: 'Find new people' },
  '/circles': { title: 'Circles', subtitle: 'Your communities' },
  '/contacts': { title: 'my ppl', subtitle: 'your contacts' },
  '/insights': { title: 'Insights', subtitle: 'what f8 learned' },
  '/profile': { title: 'Profile' },
  '/profile/edit': { title: 'Edit profile' },
  '/settings': { title: 'Settings' },
  '/onboarding': { title: 'Get started' },
  '/messages': { title: 'Messages' },
  '/premium': { title: 'Premium', subtitle: 'Upgrade your experience' },
  '/premium/callback': { title: 'Premium' },
  '/settings/subscription': { title: 'Subscription', subtitle: 'Manage your plan' },
  '/login': { title: 'Login' },
  '/admin': { title: 'Admin' },
  '/chat-history': { title: 'Chat history', subtitle: 'Your conversations with f8' },
  // PPL-17 — mediation route titles. The leaf-specific titles are picked up
  // by the prefix branch in getRouteTitle below.
  '/mediation': { title: 'Rooms', subtitle: 'conversations with f8 as a third presence' },
  '/mediation/new': { title: 'New room', subtitle: 'invite someone to a room with f8' },
  // PPL-48 — canonical /rooms/* paths mirror /mediation/*.
  '/rooms': { title: 'Rooms', subtitle: 'conversations with f8 as a third presence' },
  '/rooms/new': { title: 'New room', subtitle: 'invite someone to a room with f8' },
};

function getRouteTitle(pathname) {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  if (pathname.startsWith('/c/')) return { title: 'Circle invite' };
  if (pathname.startsWith('/circles/')) return { title: 'Circles' };
  if (pathname.startsWith('/contacts/')) return { title: ROUTE_TITLES['/contacts'].title };
  if (pathname.startsWith('/messages/')) return { title: 'Messages' };
  if (pathname.startsWith('/user/')) return { title: 'Profile' };
  if (pathname.startsWith('/talk/')) return { title: 'Posts' };
  if (pathname.startsWith('/mediation/')) return { title: 'Room' };
  if (pathname.startsWith('/rooms/')) return { title: 'Room' };
  return { title: 'pplkpr' };
}

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const useShell = isAppShellPath(location.pathname);

  // PPL-358 — the day-14 downgrade takeover: full-screen on first open after
  // the trial lapses, exactly once. Both trigger fields are server-written
  // (`trialStatus` by the expiry sweep, `downgradeTakeoverSeenAt` by the
  // summary callable); the local latch exists because the user doc in context
  // is a login-time snapshot and doesn't see the marker the callable stamps.
  const [takeoverDismissed, setTakeoverDismissed] = useState(false);
  const showTakeover =
    !takeoverDismissed &&
    !!user &&
    useShell &&
    user.trialStatus === 'expired' &&
    !user.downgradeTakeoverSeenAt &&
    (!user.selectedPlan || user.selectedPlan === 'free');

  // Manual $pageview: one code path for the initial load AND every SPA route
  // change (posthog.init runs with capture_pageview:false). Keyed on pathname
  // only so a later auth-state resolution on the same route can't double-fire.
  useEffect(() => {
    const { title } = getRouteTitle(location.pathname);
    phCapture('$pageview', {
      pathname: location.pathname,
      route_title: title,
      authenticated: !!user,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const routes = (
    <Routes>
      <Route path="/" element={<Navigate to="/f8chat" replace />} />
      <Route path="/posts" element={<Home />} />
      <Route path="/new" element={<Home />} />
      <Route path="/discussed" element={<Home />} />
      <Route path="/download" element={<Download />} />
      <Route path="/faq" element={<Faq />} />
      {/* Retired marketing pages → redirect into the app */}
      <Route path="/about" element={<Navigate to="/f8chat" replace />} />
      <Route path="/how-it-works" element={<Navigate to="/f8chat" replace />} />
      <Route path="/the-pplkpr-difference" element={<Navigate to="/f8chat" replace />} />
      <Route path="/why-pplkpr" element={<Navigate to="/f8chat" replace />} />
      <Route path="/login" element={<Login />} />
      {/* PPL-36 — bare email-signup route. Mirrors /login + /unsubscribe
          precedent (outside ProtectedRoute, outside APP_SHELL_PATHS). */}
      <Route path="/register" element={<Register />} />
      {/* PPL-36 — Firebase Auth action handler (verifyEmail, resetPassword
          fallthrough). Replaces the default
          ppldb-5cdba.firebaseapp.com/__/auth/action landing. */}
      <Route path="/auth/action" element={<VerifyEmailAction />} />
      <Route path="/dashboard" element={<Navigate to="/f8chat" replace />} />
      <Route path="/talk/:messageSlug" element={<MessageDetail />} />
      <Route path="/user/:userId" element={<UserProfile />} />
      <Route path="/f8chat" element={<F8Chat />} />
      <Route path="/chat-history" element={<ChatHistory />} />
      <Route path="/find-ppl" element={<FindPpl />} />
      <Route path="/meet-ppl" element={<Navigate to="/find-ppl" replace />} />
      <Route path="/connect-with" element={<Navigate to="/find-ppl" replace />} />
      <Route path="/circles" element={<CirclesList />} />
      <Route path="/circles/create" element={<CreateCircle />} />
      <Route path="/circles/:circleSlug" element={<CircleDetail />} />
      <Route path="/circles/:circleSlug/edit" element={<CreateCircle />} />
      <Route path="/circles/:circleSlug/members" element={<CircleMembers />} />
      <Route path="/circles/:circleSlug/chat" element={<CircleChat />} />
      <Route path="/circles/:circleSlug/events" element={<CircleEvents />} />
      <Route path="/circles/:circleSlug/events/:eventId" element={<CircleEvents />} />
      {/* PPL-395 — read view of your own profile; edit is a button on it. */}
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messages/:conversationId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/premium" element={<Premium />} />
      <Route path="/premium/callback" element={<ProtectedRoute><PremiumCallback /></ProtectedRoute>} />
      <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
      <Route path="/contacts/:contactId" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      {/* PPL-17 — public, UNAUTHENTICATED invite landing. Deliberately a bare
          route (outside ProtectedRoute, outside APP_SHELL_PATHS) — matches the
          /unsubscribe precedent. useAuthGate does not gate this; the callable
          it talks to is itself unauthenticated. Full UI lands in PPL-28. */}
      {/* PPL-42 — public mediation landing routes also get boundary protection. */}
      <Route path="/i/:token" element={<RoomErrorBoundary><RoomInviteByToken /></RoomErrorBoundary>} />
      {/* PPL-29 — public, UNAUTHENTICATED "tell me more" explainer as a
          sub-route of the bare /i/:token shell. Read-only, no callable, no
          accept/decline; the only exit is back to /i/:token. Sits outside
          ProtectedRoute and APP_SHELL_PATHS, same as /i/:token itself. */}
      <Route path="/i/:token/tell-me-more" element={<RoomErrorBoundary><RoomTellMore /></RoomErrorBoundary>} />
      {/* PPL-215 — public, UNAUTHENTICATED circle-invite landing. Same bare
          shape as /i/:token: outside ProtectedRoute and outside
          APP_SHELL_PATHS. The resolver and the decline callable behind it are
          themselves unauth; only accepting needs an account. */}
      <Route path="/c/:token" element={<CircleInviteByToken />} />
      {/* PPL-17 — authenticated /mediation/* routes. Placeholders until
          PPL-26..34 ship; URL params are intentionally encoded so downstream
          screens inherit the path shape.
          PPL-42 — each route's element is wrapped in RoomErrorBoundary so
          a render throw in any mediation screen cannot crash the app shell. */}
      {/* PPL-44 B3 — RoomList replaces the placeholder; /new redirects to f8chat (chat-driven compose). */}
      <Route path="/mediation" element={<ProtectedRoute><RoomErrorBoundary><RoomList /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/mediation/new" element={<Navigate to="/f8chat" replace />} />
      {/* PPL-44 B3 / B5 — RoomDetail: inviter-waiting + paranoid catchall. */}
      <Route path="/mediation/:mediationId" element={<ProtectedRoute><RoomErrorBoundary><RoomDetail /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-26 — invitee's "received" screen. Replaces the PPL-17 stub. */}
      <Route path="/mediation/:mediationId/invite" element={<ProtectedRoute><RoomErrorBoundary><RoomInvitationReceived /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-29 — authenticated "tell me more" explainer. Read-only; the
          only exit is back to the invitation screen. */}
      <Route path="/mediation/:mediationId/tell-me-more" element={<ProtectedRoute><RoomErrorBoundary><RoomTellMore /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-30 — solo-prep screen. Replaces the PPL-17 stub. */}
      <Route path="/mediation/:mediationId/prep" element={<ProtectedRoute><RoomErrorBoundary><RoomSoloPrep /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/mediation/:mediationId/consent" element={<ProtectedRoute><RoomErrorBoundary><RoomSharingRules /></RoomErrorBoundary></ProtectedRoute>} />
      {/* Waiting room — you've confirmed your rules; the f8 prep chat reused as a
          holding room until the other side confirms (then auto-promotes to opener). */}
      <Route path="/mediation/:mediationId/waiting" element={<ProtectedRoute><RoomErrorBoundary><RoomSoloPrep variant="waiting" /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/mediation/:mediationId/aside" element={<ProtectedRoute><RoomErrorBoundary><RoomSoloPrep variant="aside" /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-32 — joint opener ceremony. Sits between consent and the room. */}
      <Route path="/mediation/:mediationId/opener" element={<ProtectedRoute><RoomErrorBoundary><RoomOpener /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-33 — 3-voice shared room. Replaces the PPL-17 stub. */}
      <Route path="/mediation/:mediationId/room" element={<ProtectedRoute><RoomErrorBoundary><RoomSession /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-34 — check-in screen. */}
      <Route path="/mediation/:mediationId/checkin" element={<ProtectedRoute><RoomErrorBoundary><RoomCheckin /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-35 — graceful exit screen + read-only summary viewer. */}
      <Route path="/mediation/:mediationId/end" element={<ProtectedRoute><RoomErrorBoundary><RoomEnd /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/mediation/:mediationId/summary" element={<ProtectedRoute><RoomErrorBoundary><RoomSummary /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-48 — /rooms/* canonical paths. Same components as /mediation/*;
          both sets of routes coexist so old links / notifications still work. */}
      <Route path="/rooms" element={<ProtectedRoute><RoomErrorBoundary><RoomList /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/new" element={<Navigate to="/f8chat" replace />} />
      <Route path="/rooms/:mediationId" element={<ProtectedRoute><RoomErrorBoundary><RoomDetail /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/invite" element={<ProtectedRoute><RoomErrorBoundary><RoomInvitationReceived /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/tell-me-more" element={<ProtectedRoute><RoomErrorBoundary><RoomTellMore /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/prep" element={<ProtectedRoute><RoomErrorBoundary><RoomSoloPrep /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/consent" element={<ProtectedRoute><RoomErrorBoundary><RoomSharingRules /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/waiting" element={<ProtectedRoute><RoomErrorBoundary><RoomSoloPrep variant="waiting" /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/aside" element={<ProtectedRoute><RoomErrorBoundary><RoomSoloPrep variant="aside" /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/opener" element={<ProtectedRoute><RoomErrorBoundary><RoomOpener /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/room" element={<ProtectedRoute><RoomErrorBoundary><RoomSession /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/checkin" element={<ProtectedRoute><RoomErrorBoundary><RoomCheckin /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/end" element={<ProtectedRoute><RoomErrorBoundary><RoomEnd /></RoomErrorBoundary></ProtectedRoute>} />
      <Route path="/rooms/:mediationId/summary" element={<ProtectedRoute><RoomErrorBoundary><RoomSummary /></RoomErrorBoundary></ProtectedRoute>} />
      {/* PPL-407 — DEV-only fixtures for the home kit. Bare route, no shell, never in prod. */}
      {HomeFixturesPage ? (
        <Route path="/dev/home-fixtures" element={<Suspense fallback={null}><HomeFixturesPage /></Suspense>} />
      ) : null}
    </Routes>
  );

  if (useShell) {
    const { title, subtitle } = getRouteTitle(location.pathname);
    return (
      <>
        <a href="#main-content" className="skip-to-content">Skip to content</a>
        <div className="app-shell">
          <AuroraBackground />
          <Navigation user={user} collapsed={railCollapsed} onToggleCollapsed={() => setRailCollapsed(c => !c)} />
          <div className="app-main">
            <WebTopBar
              onToggleRail={() => setRailCollapsed(c => !c)}
              title={title}
              subtitle={subtitle}
            />
            <VerifyEmailBanner />
            <main id="main-content">
              {routes}
            </main>
          </div>
        </div>
        <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
        {showTakeover && <DowngradeTakeover onDone={() => setTakeoverDismissed(true)} />}
        {/* PPL-429 — the message request prompt, app-wide: one at a time, the
            oldest first, and never on top of a composer the person is using. */}
        <MessageRequestPrompt />
      </>
    );
  }

  // Standalone public pages (login, faq, download, unsubscribe)
  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <div className="App">
        <main className="main" id="main-content">
          {routes}
        </main>
      </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <MessageCacheProvider>
          {/* PPL-167 — VoiceChatProvider hoisted to the app root so every
              route (and any future voice entry point, e.g. a FAB or a /rooms
              surface) shares one provider. Previously it was scoped inside
              F8Chat, so any voice consumer rendered outside that page threw
              "useVoiceChat must be used within VoiceChatProvider". */}
          <VoiceChatProvider>
            {/* PPL-265 — one suggestions subscription feeds the rail badge,
                the insights sub-row and the /insights page, which is what
                keeps "the same number everywhere" true. */}
            <WritebackProvider>
              <AppContent />
            </WritebackProvider>
          </VoiceChatProvider>
        </MessageCacheProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
